import React, { useEffect, useState, useRef } from 'react';
import { Editor, EditorState, RichUtils, convertToRaw, Modifier, SelectionState } from 'draft-js';
import 'draft-js/dist/Draft.css';

const LimitedTextEditor = ({ fieldName, editorState, onChange, onBlur, maxChars = 1000 }) => {
	const [charCount, setCharCount] = useState(0);
	const [isExceeded, setIsExceeded] = useState(false);
	const [isAtLimit, setIsAtLimit] = useState(false);
	const editorRef = useRef(null);

	// Calculate character count from editor state
	const getCharCount = (state) => {
		if (!state) return 0;
		const contentState = state.getCurrentContent();
		const rawContent = convertToRaw(contentState);
		return rawContent.blocks.map(block => block.text).join('\n').length;
	};

	useEffect(() => {
		if (editorState) {
			const count = getCharCount(editorState);
			setCharCount(count);
			setIsExceeded(count > maxChars);
			setIsAtLimit(count === maxChars);
		}
	}, [editorState, maxChars]);

	const handleBeforeInput = (chars) => {
		const currentContent = editorState.getCurrentContent();
		const currentContentLength = getCharCount(editorState);
		const selectedTextLength = getSelectedTextLength(editorState);

		if (currentContentLength - selectedTextLength + chars.length > maxChars) {
			return 'handled';
		}

		return 'not-handled';
	};

	const getSelectedTextLength = (editorState) => {
		const selection = editorState.getSelection();
		const currentContent = editorState.getCurrentContent();

		if (selection.isCollapsed()) {
			return 0;
		}

		const startKey = selection.getStartKey();
		const endKey = selection.getEndKey();
		const startOffset = selection.getStartOffset();
		const endOffset = selection.getEndOffset();

		if (startKey === endKey) {
			return endOffset - startOffset;
		}

		let length = 0;
		let blockKey = startKey;

		while (blockKey !== endKey) {
			const block = currentContent.getBlockForKey(blockKey);
			length += block.getText().length - (blockKey === startKey ? startOffset : 0);

			const nextBlock = currentContent.getBlockAfter(blockKey);
			if (!nextBlock) break;

			blockKey = nextBlock.getKey();
		}

		const endBlock = currentContent.getBlockForKey(endKey);
		length += endOffset;

		return length;
	};

	const handlePastedText = (text) => {
		const currentContent = editorState.getCurrentContent();
		const selection = editorState.getSelection();
		const currentContentLength = getCharCount(editorState);
		const selectedTextLength = getSelectedTextLength(editorState);

		if (text && currentContentLength - selectedTextLength + text.length > maxChars) {
			if (currentContentLength - selectedTextLength < maxChars) {
				const remainingChars = maxChars - (currentContentLength - selectedTextLength);
				const textToInsert = text.substring(0, remainingChars);

				const newContentState = Modifier.replaceText(
					currentContent,
					selection,
					textToInsert
				);

				const newEditorState = EditorState.push(
					editorState,
					newContentState,
					'insert-characters'
				);

				onChange(fieldName, newEditorState);
			}

			return 'handled';
		}

		return 'not-handled';
	};

	const handleChange = (newState) => {
		const newCharCount = getCharCount(newState);

		if (newCharCount <= charCount) {
			onChange(fieldName, newState);
			return;
		}

		if (newCharCount <= maxChars) {
			onChange(fieldName, newState);
		}
	};

	const handleKeyCommand = (command) => {
		const newState = RichUtils.handleKeyCommand(editorState, command);
		if (newState) {
			handleChange(newState);
			return true;
		}
		return false;
	};

	const toggleInlineStyle = (inlineStyle) => {
		onChange(fieldName, RichUtils.toggleInlineStyle(editorState, inlineStyle));
	};

	const styleMap = {
		CODE: {
			backgroundColor: 'rgba(0, 0, 0, 0.05)',
			fontFamily: '"Inconsolata", "Menlo", "Consolas", monospace',
			fontSize: 16,
			padding: 2,
		},
	};

	const getBlockStyle = (block) => {
		switch (block.getType()) {
			case 'blockquote':
				return 'TextEditor-blockquote';
			default:
				return null;
		}
	};

	const remainingChars = maxChars - charCount;

	return (
		<div className="TextEditor-root">
			<InlineStyleControls
				editorState={editorState}
				onToggle={toggleInlineStyle}
			/>
			<div className='TextEditor-editor'>
				<Editor
					ref={editorRef}
					blockStyleFn={getBlockStyle}
					customStyleMap={styleMap}
					editorState={editorState}
					handleKeyCommand={handleKeyCommand}
					handleBeforeInput={handleBeforeInput}
					handlePastedText={handlePastedText}
					onChange={handleChange}
					placeholder=""
					spellCheck={true}
					onBlur={onBlur}
				/>
			</div>
			<div className={`char-counter ${isExceeded || remainingChars === 0 ? 'text-danger' : ''}`}>
				{remainingChars >= 0 ? remainingChars : 0} / {maxChars}
			</div>
			{isExceeded && (
				<div className="text-danger" style={{ fontSize: '12px' }}>
					თქვენ გადააჭარბეთ სიმბოლოების მაქსიმალურ რაოდენობას ({maxChars})
				</div>
			)}
		</div>
	);
};

const StyleButton = (props) => {
	const onToggle = (e) => {
		e.preventDefault();
		props.onToggle(props.style);
	};

	let className = 'TextEditor-styleButton';
	if (props.active) {
		className += ' TextEditor-activeButton';
	}
	return (
		<span className={className} onMouseDown={onToggle}>
      {props.label}
    </span>
	);
};

const INLINE_STYLES = [
	{ label: 'B', style: 'BOLD' },
	{ label: 'I', style: 'ITALIC' },
	{ label: 'U', style: 'UNDERLINE' },
];

const InlineStyleControls = (props) => {
	let currentStyle = props.editorState.getCurrentInlineStyle();
	return (
		<div className="TextEditor-controls">
			{INLINE_STYLES.map((type) => (
				<StyleButton
					key={type.label}
					active={currentStyle.has(type.style)}
					label={type.label}
					onToggle={props.onToggle}
					style={type.style}
				/>
			))}
		</div>
	);
};

export default LimitedTextEditor;